import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { useLocation } from 'react-router-dom'
import { useCallback, useEffect } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { VirtualMarketerClient } from 'api/clients/VirtualMarketerClient'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useIsPioneerStarterOrGrowth } from 'hooks/useIsPioneerStarterOrGrowth'

export const useChooseOnboardingPlan = ({ onPlanSet }: { onPlanSet: (selectedPlan: 'starter' | 'growth') => void }) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const location = useLocation()
  const workspace = useCurrentWorkspace()
  const { tierUsageStore } = useRootStore()
  const { autopilotPlans, currentSubscription, initialized, loading } = tierUsageStore
  const starterOrPioneerVariant = useIsPioneerStarterOrGrowth()
  const isGrowth = location.search.includes('campaigns=true')
  const isStarter = !isGrowth && starterOrPioneerVariant !== 'pioneer'

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, workspace, tierUsageStore, autopilotPlans.length, currentSubscription])

  const saveSubscriptionPlan = useCallback(async () => {
    if (!isOnboardingRedesign) return
    if (!isGrowth && !isStarter) return

    if (!workspace) throw new Error('No workspace')

    const targetPlanKey = isGrowth ? PaymentPlanKey.BlazeAutopilotGrowth : PaymentPlanKey.BlazeAutopilotStarter

    if (currentSubscription?.plan === targetPlanKey) {
      onPlanSet(isGrowth ? 'growth' : 'starter')

      return
    }

    // ensure the plans are loaded
    if (autopilotPlans.length === 0) {
      await tierUsageStore.fetch(workspace.id)
    }
    const plans = autopilotPlans.length > 0 ? autopilotPlans : tierUsageStore.autopilotPlans
    const monthlyPrice = plans
      .find((p) => p.key === targetPlanKey)
      ?.prices?.find((p) => p.recurring.interval_count === 1)

    if (!monthlyPrice?.id) throw new Error('Plan not found')

    const res = await VirtualMarketerClient.updateSubscription(workspace.id, {
      price_id: monthlyPrice.id,
      should_end_trial: false,
    })

    if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error('Update failed')

    await tierUsageStore.fetch(workspace.id)

    onPlanSet(isGrowth ? 'growth' : 'starter')
  }, [
    isOnboardingRedesign,
    isGrowth,
    isStarter,
    workspace,
    currentSubscription?.plan,
    autopilotPlans,
    tierUsageStore,
    onPlanSet,
  ])

  return {
    saveSubscriptionPlan,
  }
}
